/**
 * THE TEAMS A READER SEES: the one rule the sidebar and Work share.
 *
 * `/api/desks` groups the Teams this reader can read as `publishedByMe`,
 * `subscribed` (rows nest the desk under `.desk`) and `workspaceShared`, and
 * lists `restricted` ones for discovery only. A personal Team comes back to its
 * owner and to admins, marked `personal` with its owner.
 *
 * The sidebar lists the three readable groups, deduped by id, never
 * `restricted`, and a personal Team for its owner. Work and All teams list
 * exactly the same Teams, so their chips, rows and counts can never name a
 * Team the sidebar does not. A workspace owner or admin reads every Team in
 * the workspace (the backend's `reachesEveryTeam`), so the API puts every one
 * in the readable groups for them and sets `listsEveryPersonalTeam`: they are
 * shown other people's personal Teams too, as is the operator in God Mode.
 */

interface DeskRowLike {
  id?: unknown;
  personal?: unknown;
  personalOwnerUserId?: unknown;
}

/** The readable groups, in the order the sidebar has always listed them. */
const READABLE_GROUPS = ["publishedByMe", "subscribed", "workspaceShared"] as const;

function unwrap(row: unknown): Record<string, unknown> | null {
  if (!row || typeof row !== "object") return null;
  const r = row as Record<string, unknown>;
  // Subscribed rows nest the desk under `.desk`; the rest are bare desks.
  if (r.desk && typeof r.desk === "object") return r.desk as Record<string, unknown>;
  return r;
}

/**
 * The Team rows this reader sees, deduped by id, in `/api/desks` order. A bare
 * array (an older shape) is read as readable rows, with the personal rule
 * still applied.
 */
export function readableDeskRows<T extends DeskRowLike = Record<string, unknown>>(
  payload: unknown,
  viewerUserId: string | null,
): T[] {
  let raw: unknown[] = [];
  // A workspace owner or admin, and the operator in God Mode, see every Team,
  // personal ones included; the API says so rather than the client guessing
  // at roles.
  let everyPersonalTeam = false;
  if (Array.isArray(payload)) {
    raw = payload;
  } else if (payload && typeof payload === "object") {
    const body = payload as Record<string, unknown>;
    raw = READABLE_GROUPS.flatMap((group) =>
      Array.isArray(body[group]) ? (body[group] as unknown[]) : [],
    );
    everyPersonalTeam = body.listsEveryPersonalTeam === true;
  }
  const seen = new Set<string>();
  const out: T[] = [];
  for (const entry of raw) {
    const row = unwrap(entry);
    if (!row || typeof row.id !== "string" || seen.has(row.id)) continue;
    seen.add(row.id);
    // Someone else's personal Team is listed only to those who see every Team.
    if (!everyPersonalTeam && row.personal === true && row.personalOwnerUserId !== viewerUserId)
      continue;
    out.push(row as T);
  }
  return out;
}
